/**
 * Manages the item search functionality within the
 * review form. Handles real-time search, result display,
 * and integration with the review form.
 * Uses debounced search to prevent excessive API calls
 * and maintains state of already added items to prevent
 * duplicates.
 *
 * Supports different venue types (restaurant, spa,
 * catalog) via configurable resultsContainerId and
 * searchType values.
 */
import { Controller } from "@hotwired/stimulus";
import debounce from "lodash/debounce";
import { get } from "@rails/request.js";
import ToastService from "../../shared/toast_service";
import * as Sentry from "@sentry/browser";

export default class extends Controller {
  static targets = ["searchInput", "clearButton", "loading"];
  static values = {
    url: String,
    menuId: String,
    resultsContainerId: {
      type: String,
      default: "review_items_search_results",
    },
    searchType: { type: String, default: "" },
    errorMessage: {
      type: String,
      default: "An error occurred while searching. Please try again.",
    },
  };

  /**
   * Initializes the controller and sets up debounced
   * search. The debounce ensures we don't make too
   * many API calls while typing.
   */
  connect() {
    this.search = debounce(this.search.bind(this), 300);
  }

  /**
   * Updates the menu ID value dynamically.
   * Used when user selects a menu from the dropdown.
   * @param {string} menuId - The new menu ID
   */
  setMenuId(menuId) {
    this.menuIdValue = menuId;
  }

  /**
   * Handles the search input event.
   * Triggers a search with results expansion and
   * updates the clear button state.
   * @param {Event} event - The input event
   */
  search(event) {
    const query = event.target.value;
    this._performSearch(query, true);
    this.toggleClearButton();
  }

  /**
   * Performs a search without expanding the results
   * container. Used when removing items to update the
   * search state without UI changes.
   */
  searchWithoutExpandingResults() {
    const query = this.searchInputTarget.value;
    this._performSearch(query, false);
    this.toggleClearButton();
  }

  /**
   * Performs the actual search operation.
   * @param {string} query - The search query
   * @param {boolean} expandResults - Whether to
   *   expand the results container
   */
  _performSearch(query, expandResults = true) {
    const resultsContainer = document.getElementById(
      this.resultsContainerIdValue
    );

    if (query.length < 2) {
      if (resultsContainer) {
        resultsContainer.innerHTML = "";
        this.collapseResults();
      }
      return;
    }

    // Show loading spinner
    this.loadingTarget.classList.remove("hidden");

    // Get the list of already added items
    const alreadyAddedItems = document.querySelectorAll(
      "[data-review-target='item']"
    );
    const excludeIds = Array.from(alreadyAddedItems)
      .map((item) => item.querySelector("[data-reviewable-id]")?.value)
      .filter(Boolean)
      .join(",");

    get(
      `${this.urlValue}?query=${encodeURIComponent(query)}&menu_id=${this.menuIdValue}&exclude_ids=${excludeIds}&expand_results=${expandResults}&search_type=${this.searchTypeValue}&container_id=${this.resultsContainerIdValue}`,
      {
        responseKind: "turbo-stream",
      }
    )
      .then((response) => {
        // Hide loading spinner
        this.loadingTarget.classList.add("hidden");

        if (response.ok && expandResults) {
          requestAnimationFrame(() => {
            this.expandResults();
          });
        } else if (!response.ok) {
          console.error(response);
          new ToastService().toast({
            text: this.errorMessageValue,
            type: "error",
          });
        }
      })
      .catch((error) => {
        // Hide loading spinner
        this.loadingTarget.classList.add("hidden");

        console.error(error);
        new ToastService().toast({
          text: this.errorMessageValue,
          type: "error",
        });

        Sentry.captureException(error);
      });
  }

  /**
   * Handles focus on the search input.
   * Expands results if there are any and updates the
   * clear button state.
   */
  focus() {
    const resultsContainer = document.getElementById(
      this.resultsContainerIdValue
    );
    if (!resultsContainer) return;

    if (resultsContainer.children.length > 0) {
      this.expandResults();
    }
    this.toggleClearButton();
  }

  /**
   * Expands the search results container and scrolls
   * it into view. Uses smooth scrolling for better UX.
   */
  expandResults() {
    const searchWrapper = this.element.closest(
      "[data-review-item-search-wrapper]"
    );
    if (searchWrapper) {
      searchWrapper.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    document
      .getElementById(this.resultsContainerIdValue)
      ?.classList.add("expanded");
  }

  /**
   * Collapses the search results container.
   */
  collapseResults() {
    const resultsContainer = document.getElementById(
      this.resultsContainerIdValue
    );
    if (resultsContainer) {
      resultsContainer.classList.remove("expanded");
    }
  }

  /**
   * Toggles the visibility of the clear button based
   * on input state. Shows the button when there's text
   * or the input is focused.
   */
  toggleClearButton() {
    const hasText =
      this.searchInputTarget.value.trim().length > 0;
    const isFocused =
      document.activeElement === this.searchInputTarget;
    const shouldShow = hasText || isFocused;

    this.clearButtonTarget.classList.toggle(
      "opacity-0", !shouldShow
    );
    this.clearButtonTarget.classList.toggle(
      "pointer-events-none", !shouldShow
    );
  }

  /**
   * Clears the search input without losing focus in
   * certain cases.
   * @param {Event} event - The mousedown event
   */
  clearSearchWithoutFocus(event) {
    event.preventDefault();
    event.stopPropagation();

    const wasFocused =
      document.activeElement === this.searchInputTarget;
    const hadText =
      this.searchInputTarget.value.trim().length > 0;

    this.searchInputTarget.value = "";
    this._performSearch("", false);

    if (wasFocused && hadText) {
      this.searchInputTarget.focus();
    } else if (wasFocused && !hadText) {
      this.searchInputTarget.blur();
    } else {
      this.toggleClearButton();
    }
  }
}
